import { FC, memo, MutableRefObject, useCallback, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { setBodyOverflow } from 'src/helpers/utils';
import { useAutoplayInView } from 'src/hooks/useAutoplayInView';
import { CmsGameReplayGroup, CmsGameReplayItem } from 'src/types/cms/cmsWidgetTypes';
import SwiperCore from 'swiper';
import 'swiper/css';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import './styles.scss';

type WinReplaysSliderProps = {
  replays: CmsGameReplayItem[];
  onSelect: (src: string) => void;
  swiperRef: MutableRefObject<SwiperCore | undefined>;
  overlayOpenRef: MutableRefObject<boolean>;
};

const areReplaysEqual = (a: CmsGameReplayItem[], b: CmsGameReplayItem[]): boolean =>
  a.length === b.length && a.every((r, i) => r?.videoUrl === b[i]?.videoUrl);

// Content-memoized: re-render only when the replay list changes, else the loop shuffles.
const WinReplaysSlider = memo(
  ({ replays, onSelect, swiperRef, overlayOpenRef }: WinReplaysSliderProps) => {
    const slidesPerView = isDesktop ? 12.5 : 3.5;
    const enableLoop = isDesktop && replays.length >= Math.ceil(slidesPerView) + 1;

    // pause autoplay out of view; never resume a static row or while the overlay covers it
    const listRef = useAutoplayInView<HTMLDivElement>(swiperRef, () => enableLoop && !overlayOpenRef.current);

    const orderKey = replays.map((r) => r?.videoUrl).join(',');

    return (
      <div
        ref={listRef}
        className="winReplaysList"
        onMouseEnter={() => {
          if (swiperRef.current) {
            requestAnimationFrame(() => swiperRef.current?.autoplay?.stop());
          }
        }}
        onMouseLeave={() => {
          // don't resume a static row, or while the overlay is open (it covers the cursor and fires mouseleave)
          if (swiperRef.current && enableLoop && !overlayOpenRef.current) {
            requestAnimationFrame(() => swiperRef.current?.autoplay?.start());
          }
        }}
      >
        <Swiper
          key={orderKey}
          onSwiper={(swiper: SwiperCore) => {
            swiperRef.current = swiper;
            // a remount (resize) can happen while the overlay is open; keep the marquee frozen
            if (overlayOpenRef.current) {
              requestAnimationFrame(() => swiper.autoplay?.stop());
            }
          }}
          modules={[Autoplay]}
          slidesPerView={slidesPerView}
          slidesPerGroup={1}
          spaceBetween={10}
          speed={3000}
          loop={enableLoop}
          autoplay={
            enableLoop
              ? {
                  delay: 0,
                  disableOnInteraction: false,
                }
              : false
          }
        >
          {replays.map((replay, index) => (
            <SwiperSlide key={index}>
              <button onClick={() => onSelect(replay?.videoUrl)} className="winReplaysItemWrapper">
                <div className="winReplaysItem">
                  <img className="winReplaysItemImage" src={replay?.thumbnail?.path} alt="" loading="lazy" />
                </div>
                <span className="winReplaysItemName">{replay?.multiplierText}</span>
              </button>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  },
  (prev, next) =>
    prev.onSelect === next.onSelect && prev.swiperRef === next.swiperRef && areReplaysEqual(prev.replays, next.replays)
);

WinReplaysSlider.displayName = 'WinReplaysSlider';

const WinReplaysWidget: FC<{ winReplaysData?: CmsGameReplayGroup | null }> = ({ winReplaysData }) => {
  const swiperRef = useRef<SwiperCore>();
  const overlayOpenRef = useRef<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);

  const openWinReplaysVideo = useCallback((src: string): void => {
    setSelectedVideo(src);
    setIsReady(false);
    // freeze the marquee while the overlay is open so the row stays put on close
    overlayOpenRef.current = true;
    swiperRef.current?.autoplay?.stop();
  }, []);

  const handleCloseVideo = (): void => {
    setBodyOverflow('set');
    setSelectedVideo(null);
    setIsReady(false);
    overlayOpenRef.current = false;
    // only resume a looping row: Swiper's autoplay.start() ignores the disabled flag and would crawl a static row
    if (swiperRef.current?.params?.loop) swiperRef.current.autoplay?.start();
  };

  return winReplaysData?.gameReplays ? (
    <div className="winReplays">
      <WinReplaysSlider
        replays={winReplaysData.gameReplays}
        onSelect={openWinReplaysVideo}
        swiperRef={swiperRef}
        overlayOpenRef={overlayOpenRef}
      />
      {selectedVideo && (
        <div className="winReplays__video">
          <iframe
            src={selectedVideo}
            title="Win Replay"
            frameBorder="0"
            onLoad={() => {
              setIsReady(true);
              setBodyOverflow('unset');
            }}
          />
          {isReady && <SvgIcon src={icons.close} onClick={handleCloseVideo} className="close-game" />}
        </div>
      )}
    </div>
  ) : null;
};

export default WinReplaysWidget;
